import { useEffect, useMemo, useState } from "react";
import DataTable, { TableColumn } from "react-data-table-component";
import { useNavigate } from "react-router-dom";
import ApplicationsFilter, {
  ApplicationFilterState,
} from "../../components/ApplicationsFilter/ApplicationsFilter";
import BoxComponent from "../../components/CommonComponents/BoxComponent/BoxComponent";
import { Dialog } from "../../components/CommonComponents/Dialog/Dialog";
import { ViewButton } from "../../components/CommonComponents/EditComponent/EditComponent";
import Paginator, {
  PaginatorForRDT,
} from "../../components/CommonComponents/Paginator/Paginator";
import SortIcon from "../../components/CommonComponents/SortIcon/SortIcon";
import { tableCustomStyles } from "../../components/CommonComponents/Table/Table";
import { isLevel2 } from "../../hoc/isLevel2";
import { TApplication } from "../../hooks/applications.hooks";
import { useDebounce, useToggle } from "../../hooks/common";
import {
  useApplicationCountForLevel2,
  useApplicationsForLevel2,
} from "../../hooks/level-2-application-approval.hooks";
import { formatDate } from "../../utils/date";
import {
  statusesApplicableForPaymentScreen,
  validLevel2Actionable,
} from "./LevelTwo.constants";

export const LevelTwoApplications = isLevel2(() => {
  const navigate = useNavigate();
  const [filterOpen, toggleFilter] = useToggle();
  const [filterState, setFilterState] = useState<ApplicationFilterState>({
    connector: "and",
    conditions: [],
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [view, setView] = useState<"table" | "card">("table");
  const [page, setPage] = useState(1);
  const search = useDebounce(searchQuery);
    const [{ sortKey, sortOrder }, setSort] = useState({
        sortKey: "__default__",
        sortOrder: -1,
    });
  const { isLoading: isLoading1, data: counts } = useApplicationCountForLevel2({
    search,
    filters: filterState,
  });
  const { isLoading: isLoading2, data } = useApplicationsForLevel2({
    page,
    search,
    limit: 10,
    sort: sortKey,
    sortVal: sortOrder,
    filters: filterState,
  });

  useEffect(() => {
    if (data?.data) {
      console.log("🔥 RAW Level-2 API DATA ↓↓↓");
      console.table(
        data.data.map((a) => ({
          id: a.id,
          applicationId: a.applicationId,
          status: a.status,
          level2QueueAt: a.level2QueueAt,
          level2SubmissionDate: a.level2SubmissionDate,
          isActionAllowed: a.isActionAllowed,
        }))
      );
    }
  }, [data]);

    useEffect(() => {
        if (data?.data) {
            console.log("====== FRONTEND RECEIVED DATA ======");

            console.table(
                data.data.map((a, index) => ({
                    index,
                    id: a.id,
                    applicationId: a.applicationId,
                    status: a.status,
                    level2QueueAt: a.level2QueueAt,
                    level2SubmissionDate: a.level2SubmissionDate,
                    isActionAllowed: a.isActionAllowed,
                }))
            );
        }
    }, [data]);

  const columns = useMemo(
    (): TableColumn<TApplication & { serviceName?: string }>[] => [
      {
        name: "Application ID",
        sortable: true,
        width: "190px",
        sortField: "applicationId",
        selector: (d) => d.applicationId,
        center: true,
        grow: 0.5,
        cell: (e) => {
          const disabled = !e.isActionAllowed;

          return (
            <span
              className={`font-semibold ${disabled
                ? "text-gray-400 cursor-not-allowed"
                : "text-[#E9B925] cursor-pointer underline"
                }`}
              onClick={() => {
                if (disabled) return;

                if (statusesApplicableForPaymentScreen.includes(e.status)) {
                  navigate(`/level-2-payment-details`, { state: { id: e.id } });
                } else if (validLevel2Actionable.includes(e.status)) {
                  navigate(`/level-2-application-details`, {
                    state: { id: e.id },
                  });
                } else {
                  navigate(`/view-application-details`, {
                    state: { id: e.id },
                  });
                }
              }}
            >
              {e.applicationId}
            </span>
          );
        },
      },
      {
        name: "Plot Owner's Name",
        sortable: true,
        sortField: "plot_owner_s_name",
        selector: (d) => d.plot_owner_s_name || "-",
        cell: (d) => (
          <span className="text-center">{d.plot_owner_s_name || "-"}</span>
        ),
        center: true,
        grow: 2,
      },
      {
        name: "Name of Service",
        width: "210px",
        sortable: false,
        sortField: "name_of_service",
        selector: (d) => d.serviceName || "-",
        cell: (d) => (
          <span className="text-center">{d.serviceName || "-"}</span>
        ),
        center: true,
      },
      {
        name: "Status",
        width: "205px",
        center: true,
        sortable: true,
        sortField: "status",
        cell: (d) => <span className="text-center">{d.status}</span>,
      },
      {
        name: "Days Left for Action",
       // sortable: false,
        //sortField: "daysLeftForAction",
        width: "150px",
        cell: (d) => {
          console.log(`Rendering days left for ${d.applicationId}:`, d.daysLeftForAction);

          // If no value, show "-"
          if (d.daysLeftForAction === null || d.daysLeftForAction === undefined) {
            return <span className="text-center">-</span>;
          }

          // If days left is 0, display with urgent styling
          if (d.daysLeftForAction === 0) {
            return <span className="text-center text-red-600 font-bold">0</span>;
          }

          // Normal case with days remaining
          const isDanger = d.daysLeftForAction <= 2; // Less than or equal to 2 days is urgent
          return (
            <span className={`text-center ${isDanger ? 'text-red-600 font-bold' : ''}`}>
              {d.daysLeftForAction}
            </span>
          );
        },
        center: true,
      },
      {
        name: "Date of Submission",
        width: "220px",
        center: true,
        sortable: true,
        sortField: "submittedAt",
        cell: (d) => (d.submittedAt ? formatDate(d.submittedAt) : "-"),
      },
      {
        name: "Created By",
        selector: (row: any) => row.createdBy,
        cell: (row: any) => (
          <span className="text-center">{row.createdBy}</span>
        ),
        center: true,
        sortable: true,
          sortField: "createdBy",
        width: "170px",
      },
      {
        name: "Created On",
        selector: (row: any) => formatDate(new Date(row.createdAt)),
        center: true,
          sortField: "createdAt",
        sortable: true,
        width: "170px",
      },
      {
        name: "Modified By",
        selector: (row: any) => row.modifiedBy,
        cell: (row: any) => (
          <span className="text-center">{row.modifiedBy}</span>
        ),
        center: true,
          sortField: "modifiedBy",
        sortable: true,
        width: "170px",
      },
      {
        name: "Modified On",
        selector: (row: any) => formatDate(new Date(row.updatedAt)),
        center: true,
          sortField: "updatedAt",
        sortable: true,
        width: "170px",
      },
      {
        name: "Action",
        center: true,
        width: "127px",
        grow: 0.5,
        cell: (e) => {
          const disabled = !e.isActionAllowed;

          return (
            <div className="flex justify-center">
              <ViewButton
                disabled={disabled}
                onClick={() => {
                  if (disabled) return;

                  if (statusesApplicableForPaymentScreen.includes(e.status)) {
                    navigate(`/level-2-payment-details`, { state: { id: e.id } });
                  } else if (validLevel2Actionable.includes(e.status)) {
                    navigate(`/level-2-application-details`, {
                      state: { id: e.id },
                    });
                  } else {
                    navigate(`/view-application-details`, {
                      state: { id: e.id },
                    });
                  }
                }}
              />
            </div>
          );
        },
      },
    ],
    [navigate]
  );

  return (
    <div className="p-4 md:p-8">
      <div className="flex justify-between mb-4 flex-wrap md:flex-nowrap gap-4 items-center">
        <h4 className="text-2xl font-bold">All Applications</h4>
        <div className="flex space-x-4 items-center grow md:grow-0">
          <button
            className="p-3 bg-white border border-[#968f8f] rounded-xl flex justify-center items-center"
            onClick={() => setView((e) => (e === "card" ? "table" : "card"))}
          >
            {view === "table" ? (
              <i className="fa fa-th text-primary m-0" />
            ) : (
              <i className="fa fa-list-ul text-primary m-0" />
            )}
          </button>
          <button
            className="p-3 bg-white border border-[#968f8f] rounded-xl flex justify-center items-center min-w-[42px]"
            onClick={toggleFilter}
          >
            <i className="fa fa-filter text-primary m-0" />
          </button>
          <div className="grow md:grow-0 flex rounded-xl border border-[#968f8f] bg-white items-stretch overflow-hidden">
            <input
              type="text"
              placeholder="Search..."
              className="bg-none px-3"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />

            <div className="p-3 shadow-md flex justify-center items-center">
              <i className="fa fa-search text-primary m-0" />
            </div>
          </div>
        </div>
      </div>
      {view === "table" ? (
        <BoxComponent>
          <DataTable
            pagination
            paginationServer
            className="mt-4 rounded-0"
            columns={columns}
            data={data?.data || []}
            sortServer
            sortIcon={<SortIcon />}
            customStyles={tableCustomStyles}
            onChangePage={(p) => setPage(p)}
            paginationTotalRows={counts?.data || 0}
            progressPending={isLoading1 || isLoading2}
            onSort={(col, dir) => {
                if (!col?.sortField) return;

                setSort({
                    sortKey: col.sortField,
                    sortOrder: dir === "asc" ? 1 : -1,
                });
            }}
            // paginationComponentOptions={{
            //   noRowsPerPage: true,
            //   selectAllRowsItem: false,
            // }}
            paginationComponent={PaginatorForRDT}
          />
        </BoxComponent>
      ) : (
        <>
          <BoxComponent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {(data?.data || []).map((e) => {
                    const disabled = !e.isActionAllowed;
                return (
                  <div
                    className="p-4 rounded-2xl bg-white shadow-lg flex flex-col border border-gray-300"
                    key={e.id}
                  >
                    <div className="flex flex-col h-full">
                      <div className="flex-grow">
                        <div className="flex justify-between items-center mb-4">
                          <div className="text-center bg-yellow-500 text-grey-600 inline-block py-1 px-4 rounded-full text-sm lg:text-base font-semibold">
                            {e.status}
                          </div>
                          <div className="parent-container">
                            <div className="flex space-x-2">
                              <ViewButton
                                disabled={disabled}
                                onClick={() => {
                                  if (disabled) return;

                                  if (statusesApplicableForPaymentScreen.includes(e.status)) {
                                    navigate(`/level-2-payment-details`, { state: { id: e.id } });
                                  } else if (validLevel2Actionable.includes(e.status)) {
                                    navigate(`/level-2-application-details`, {
                                      state: { id: e.id },
                                    });
                                  } else {
                                    navigate(`/view-application-details`, {
                                      state: { id: e.id },
                                    });
                                  }
                                }}
                              />

                            </div>
                          </div>
                        </div>
                        <div className="mb-2 flex">
                          <p className="font-bold text-base w-1/2">
                            Application ID
                          </p>
                          <p className="text-base mx-2">:</p>
                          <p className="text-base w-2/3">{e.applicationId}</p>
                        </div>
                        <div className="mb-2 flex">
                          <p className="font-bold text-base w-1/2">
                            Plot Owner's Name
                          </p>
                          <p className="text-base mx-2">:</p>
                          <p className="text-base w-2/3">
                            {e.plot_owner_s_name || "-"}
                          </p>
                        </div>
                        <div className="mb-2 flex">
                          <p className="font-bold text-base w-1/2">
                            Date of Submission
                          </p>
                          <p className="text-base mx-2">:</p>
                          <p className="text-base w-2/3">
                            {formatDate(e.submittedAt || "x")}
                          </p>
                        </div>
                        <div className="mb-2 flex">
                          <p className="font-bold text-base w-1/2">Days Left for Action</p>
                          <p className="text-base mx-2">:</p>
                          {e.daysLeftForAction === null || e.daysLeftForAction === undefined ? (
                            <p className="text-base w-2/3">-</p>
                          ) : e.daysLeftForAction === 0 ? (
                            <p className="text-base w-2/3 text-red-600 font-bold">0</p>
                          ) : (
                            <p className={`text-base w-2/3 ${e.daysLeftForAction <= 2 ? 'text-red-600 font-bold' : ''}`}>
                              {e.daysLeftForAction}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
            <Paginator
              limit={10}
              page={page}
              setPage={setPage}
              count={counts?.data || 0}
            />
          </BoxComponent>
        </>
      )}
      <Dialog
        onClose={toggleFilter}
        open={filterOpen}
        className="flex flex-col sm:w-[600px] lg:w-[800px]"
      >
        <ApplicationsFilter
          onClose={toggleFilter}
          defaultFilter={filterState}
          onChange={setFilterState}
        />
      </Dialog>
    </div>
  );
});
